/**
 * probeRecords — the records each of the examiner's lookups returned, by the
 * ids the server gave them, so the page can match them with the briefing's.
 *
 * @module state/probeRecords
 */
import {
  PROBE_BRIEFING_PASSAGE_MAX_CHARS,
  PROBE_WEB_SEARCH_TOOL,
  ProbeWebSearchOutput,
  type ProbeBriefingPassage,
  type ProbeRecord,
  type ProbeToolName,
} from "@amoreg/contracts";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";

import { safeExternalHref } from "../lib/safeExternalHref";
import { linkableSource } from "./probeSession";

export interface ProbeRecordRead {
  readonly callId: string;
  readonly atMs: number;
  readonly record: ProbeRecord;
}

export type ProbeToolArguments = Readonly<Record<string, unknown>>;

export const parseToolArguments = (raw: string): ProbeToolArguments => {
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as ProbeToolArguments)
      : {};
  } catch {
    return {};
  }
};

const oneLine = (text: string): string => text.replace(/\s+/g, " ").trim();

/** The briefing's clip keeps one character fewer than its limit, and may trim a space before the "…". */
const PASSAGE_KEY_CHARS = PROBE_BRIEFING_PASSAGE_MAX_CHARS - 2;

const fnv1a = (text: string): string => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index++) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
};

export const passageKey = ({
  source,
  text,
}: Pick<ProbeBriefingPassage, "source" | "text">): string =>
  fnv1a(`${source ?? ""}\u0000${oneLine(text).slice(0, PASSAGE_KEY_CHARS)}`);

const Finding = Schema.Struct({
  id: Schema.String,
  title: Schema.String,
  severity: Schema.NullOr(Schema.String),
  status: Schema.String,
});
const Findings = Schema.Struct({ findings: Schema.Array(Finding) });
const Policy = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  governed: Schema.optionalKey(Schema.Boolean),
  lifecycleStatus: Schema.optionalKey(Schema.NullOr(Schema.String)),
  ownerEmail: Schema.optionalKey(Schema.NullOr(Schema.String)),
  reviewDueAt: Schema.optionalKey(Schema.NullOr(Schema.String)),
});
const Policies = Schema.Struct({ policies: Schema.Array(Policy) });
const PolicyRecord = Schema.Struct({ policy: Policy, findings: Schema.Array(Finding) });
const PolicyText = Schema.Struct({ policyId: Schema.String });
const Updates = Schema.Struct({
  updates: Schema.Array(
    Schema.Struct({
      id: Schema.String,
      title: Schema.String,
      jurisdictionName: Schema.String,
      sourceUrl: Schema.NullOr(Schema.String),
      publishedAt: Schema.NullOr(Schema.String),
      detectedAt: Schema.String,
    }),
  ),
});
const Passages = Schema.Struct({
  passages: Schema.Array(
    Schema.Struct({
      source: Schema.NullOr(Schema.String),
      sectionPath: Schema.NullOr(Schema.String),
      content: Schema.String,
    }),
  ),
});
const Interview = Schema.Struct({
  id: Schema.String,
  title: Schema.String,
  createdAt: Schema.String,
});
const Interviews = Schema.Struct({ interviews: Schema.Array(Interview) });
const InterviewRecord = Schema.Struct({ interview: Interview });

const findingRef = (row: typeof Finding.Type): ProbeRecord => ({
  kind: "finding",
  id: row.id,
  title: row.title,
  severity: row.severity || null,
  status: row.status,
});

const policyRef = (row: typeof Policy.Type): ProbeRecord => ({
  kind: "policy",
  id: row.id,
  title: row.name,
  facts: {
    lifecycle: row.governed === false ? "not under governance" : row.lifecycleStatus || null,
    owner: row.ownerEmail || null,
    reviewDue: row.reviewDueAt?.slice(0, 10) || null,
  },
});

const rehearsalRef = (row: typeof Interview.Type): ProbeRecord => ({
  kind: "rehearsal",
  id: row.id,
  title: row.title,
  date: row.createdAt.slice(0, 10),
});

type Extractor = (args: ProbeToolArguments, output: unknown) => ReadonlyArray<ProbeRecord>;

const reading = <A>(
  schema: Schema.ConstraintDecoder<A>,
  records: (output: A) => ReadonlyArray<ProbeRecord>,
): Extractor => {
  const decode = Schema.decodeUnknownOption(schema);
  return (_args, output) => Option.match(decode(output), { onNone: () => [], onSome: records });
};

const nothing: Extractor = () => [];

export const PROBE_RECORD_EXTRACTORS: Readonly<Record<ProbeToolName, Extractor>> = {
  governance_get_context: nothing,
  governance_list_people: nothing,
  governance_list_recommendations: nothing,
  governance_list_findings: reading(Findings, ({ findings }) => findings.map(findingRef)),
  governance_list_policies: reading(Policies, ({ policies }) => policies.map(policyRef)),
  governance_get_policy: reading(PolicyRecord, ({ policy, findings }) => [
    policyRef(policy),
    ...findings.map(findingRef),
  ]),
  governance_get_policy_text: reading(PolicyText, ({ policyId }) => [
    { kind: "policy", id: policyId, title: null, facts: null },
  ]),
  governance_list_regulatory_updates: reading(Updates, ({ updates }) =>
    updates.map((row) => ({
      kind: "update",
      id: row.id,
      title: row.title,
      date: (row.publishedAt ?? row.detectedAt).slice(0, 10) || null,
      jurisdiction: row.jurisdictionName,
      url: safeExternalHref(row.sourceUrl) === null ? null : row.sourceUrl,
    })),
  ),
  governance_search_knowledge: reading(Passages, ({ passages }) =>
    passages.flatMap(({ source: named, sectionPath, content }) => {
      const text = oneLine(content);
      if (text.length === 0) return [];
      const source = named || null;
      return [
        {
          kind: "passage",
          id: passageKey({ source, text }),
          title: source ?? (sectionPath || text.slice(0, 80)),
          source,
          text: text.slice(0, PROBE_BRIEFING_PASSAGE_MAX_CHARS),
        },
      ];
    }),
  ),
  governance_list_probe_interviews: reading(Interviews, ({ interviews }) =>
    interviews.map(rehearsalRef),
  ),
  governance_get_probe_interview: reading(InterviewRecord, ({ interview }) => [
    rehearsalRef(interview),
  ]),
  [PROBE_WEB_SEARCH_TOOL]: reading(ProbeWebSearchOutput, ({ results }) =>
    results.flatMap(({ title, url }) => {
      const page = linkableSource(title, url);
      return page === null ? [] : [{ kind: "web", id: page.url, ...page }];
    }),
  ),
};

export const recordsOf = (
  name: string,
  args: ProbeToolArguments,
  output: unknown,
): ReadonlyArray<ProbeRecord> =>
  Object.hasOwn(PROBE_RECORD_EXTRACTORS, name)
    ? PROBE_RECORD_EXTRACTORS[name as ProbeToolName](args, output)
    : [];

export const recordKey = (record: ProbeRecord): string => `${record.kind}:${record.id}`;

export const recordsRead = (
  reads: ReadonlyArray<ProbeRecordRead>,
): ReadonlyArray<ProbeRecordRead> => {
  const first = new Map<string, ProbeRecordRead>();
  for (const read of reads) {
    const key = recordKey(read.record);
    const seen = first.get(key);
    if (seen === undefined) first.set(key, read);
    else if (
      seen.record.kind === "policy" &&
      read.record.kind === "policy" &&
      (seen.record.title === null || seen.record.facts === null)
    ) {
      first.set(key, {
        ...seen,
        record: {
          ...seen.record,
          title: seen.record.title ?? read.record.title,
          facts: seen.record.facts ?? read.record.facts,
        },
      });
    }
  }
  return [...first.values()];
};
